import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import { produce } from 'immer';
import { ModalDialog } from 'components/ModalDialog';
import { TotalNetDownPaymentPanel } from 'components/Modals/FrontAndBackEndsAddsModal/styles';
import { CoApplicantHint } from 'modules/RACAffordabilityEngine/RacMontlyIncomeModal/styles';
import { MODAL_DIALOG_WIDTH } from 'modules/core/constants';
import { ColumnWidth } from 'modules/core/enums';
import { DollarAdornment, FormRow, Input, MoneyLabel, Panel } from 'modules/core/components';

import { EstimateHint, EstimateRow, EstimateValue } from './styles';
import { LOAN_VARIABLES_FIELDS_NAMES, LOAN_VARIABLES_FORM_PREFIX } from '../../../consts/fieldNames.const';
import { getBackAddsEstimate } from './utils';

const ADDITIONAL_ADVANCE = 'additionalAdvance';

interface IBackAddsCalculatorProps {
  show: boolean;
  onHide: () => void;
  linePolicy?: number | null;
  line3?: number | null;
  edc?: number | null;
  maxBackEnd?: number | null;
  setFormHistory: React.Dispatch<React.SetStateAction<any[]>>;
}

export const BackAddsCalculator: FC<IBackAddsCalculatorProps> = ({
  show,
  onHide,
  linePolicy,
  line3,
  edc,
  maxBackEnd,
  setFormHistory,
}) => {
  const { t } = useTranslation();
  const { setValue: commonSetValue, getValues: commonGetValues } = useFormContext();
  const formMethods = useForm({ mode: 'onSubmit', shouldUnregister: false });
  const { handleSubmit, watch } = formMethods;

  const estimate = getBackAddsEstimate({
    policy: linePolicy,
    line3,
    edc,
    advance: watch(ADDITIONAL_ADVANCE),
    maxBackEnd,
  });

  const onSubmit = useCallback(() => {
    if (estimate === null) return;

    commonSetValue(LOAN_VARIABLES_FIELDS_NAMES.VSC, estimate, { shouldDirty: true, shouldValidate: true });
    setFormHistory((prevHistory) =>
      produce(prevHistory, (draft) => {
        draft.push({ loanVariablesForm: commonGetValues()[LOAN_VARIABLES_FORM_PREFIX] });
      }),
    );
    onHide();
  }, [estimate, commonSetValue, commonGetValues, setFormHistory, onHide]);

  return (
    <FormProvider {...formMethods}>
      <ModalDialog
        show={show}
        onHide={onHide}
        keyboard={false}
        modalSize={MODAL_DIALOG_WIDTH.CUSTOM.PX_500}
        verticallyCentered={true}
        title={t('components.loanVariablesModal.modals.backAdds.title')}
        upperCaseTitle={true}
        cancelButtonTitle={t('common.buttons.cancel')}
        submitButtonTitle={t('common.buttons.apply')}
        onSubmitModal={handleSubmit(onSubmit)}
        isDisabled={estimate === null}
      >
        <Form onSubmit={(event) => event.preventDefault()}>
          <TotalNetDownPaymentPanel>
            <EstimateRow>
              <FormRow
                fieldWidth={ColumnWidth.S}
                label={t('components.loanVariablesModal.modals.backAdds.estimated')}
                control={
                  <EstimateValue>
                    <MoneyLabel isInteger isColored dataTestId="back-adds-estimate">
                      {estimate ?? 0}
                    </MoneyLabel>
                    <EstimateHint>{t('components.loanVariablesModal.modals.backAdds.subjectToApproval')}</EstimateHint>
                  </EstimateValue>
                }
              />
            </EstimateRow>
          </TotalNetDownPaymentPanel>
          <Panel>
            <FormRow
              fieldWidth={ColumnWidth.S}
              required={true}
              label={t('components.loanVariablesModal.modals.backAdds.additionalAdvance')}
              name={ADDITIONAL_ADVANCE}
              control={
                <Input
                  name={ADDITIONAL_ADVANCE}
                  startAdornment={<DollarAdornment />}
                  placeholder={t('components.input.placeholders.integer')}
                  textStyle="bold"
                  decimalScale={0}
                />
              }
            />
            <CoApplicantHint>{t('components.loanVariablesModal.modals.backAdds.disclaimer')}</CoApplicantHint>
          </Panel>
        </Form>
      </ModalDialog>
    </FormProvider>
  );
};
